import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import ArthaMultiSelect from "../../fields/ArthaMultiSelect";
import ArthaCancelButton from "../../fields/ArthaCancelButton";
import Select from "react-select";
import DatePicker from "react-datepicker";
import { handleDatePickerRawChange, dateFormatMaxLength } from "../../fields/datePickerInput";
import AddMember from "./AddMember";
import { ReactComponent as UserImage } from "../../Assets/mobileUser.svg";
import ArthaContinueButton from "../../fields/ArthaContinueButton";
import { toast } from "react-toastify";
import jsPDF from "jspdf";
import autoTable from 'jspdf-autotable';
import "react-datepicker/dist/react-datepicker.css";
import "./dashboard.css";
import {
  fetchMemberApi,
  generateReport,
  fetchOrganizationUsers,
} from "../../actioncreators/actioncreators";
import {
  Search,
  Settings,
  Download,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { useSuspension } from "../../contexts/SuspensionContext";

function EmployeeReportTracking() {
  // Suspension check
  const { isSuspended } = useSuspension();
  const globalLocationFilter = useSelector((state) => state.selectedLocationFilter);
  const [isReportJustGenerated, setIsReportJustGenerated] = useState(false);
  const [isDownloadEnabled, setIsDownloadEnabled] = useState(false);
  const [hasGeneratedReport, setHasGeneratedReport] = useState(false);
  const [selectedMemberId, setSelectedMemberId] = useState(null); // State to track selected member
  const [fromDate, setFromDate] = useState(new Date());
  const [toDate, setToDate] = useState(new Date());
  const [selectedUser, setSelectedUser] = useState(null); // Replace selectedUsers state with this
  const [users, setUsers] = useState([]); // Add this state for users list
  const [isFirstAccordionOpen, setIsFirstAccordionOpen] = useState(true);
  const [isSecondAccordionOpen, setIsSecondAccordionOpen] = useState(true); // Changed to true
  const [subAccordions, setSubAccordions] = useState({
    first: true,
    second: true,
    third: true,
    fourth: true,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [reportData, setReportData] = useState(null);

  const recordsPerPage = 20;
  const totalPages = reportData?.summaryData?.daily
    ? Math.ceil(reportData.summaryData.daily.length / recordsPerPage)
    : 0;

  const currentTableData = reportData?.summaryData?.daily
    ? reportData.summaryData.daily.slice(
        (currentPage - 1) * recordsPerPage,
        currentPage * recordsPerPage
      )
    : [];

  console.log("currentTableData", currentTableData);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await fetchOrganizationUsers(globalLocationFilter);
        if (response?.businessStatusCode === 2) {
          const userOptions =
            response.data?.map((user) => ({
              name: user.full_name,
              code: user.user_id,
            })) || [];
          console.log("userOptions", userOptions);
          setUsers(userOptions);
          // Reset selected user when location filter changes
          setSelectedUser(null);
        }
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    };
    fetchUsers();
  }, [globalLocationFilter]);

 

  const handleGenerateReport = async () => {
    if (!selectedUser) {
      toast.error("Please select a user");
      return;
    }

    const payload = {
      fromDate: fromDate.toISOString().split("T")[0],
      toDate: toDate.toISOString().split("T")[0],
      userId: selectedUser.code,
    };

    try {
      const response = await generateReport(payload);

      if (response?.businessStatusCode === 2) {
        toast.success("Report generated successfully!");
        // Set the entire response data
        console.log("response", response);
        setReportData(response);
        console.log("reportData", reportData);
        setIsReportJustGenerated(true);
        setIsDownloadEnabled(true); // Enable download button after successful report generation
        setHasGeneratedReport(true); // Set this flag to true after first successful generation
      }
    } catch (error) {
      console.error("Error generating report:", error);
      toast.error("Failed to generate report");
    }
  };

  useEffect(() => {
    if (isReportJustGenerated) {
      setIsReportJustGenerated(false);
      // Only disable download if we haven't generated a report before
      if (!hasGeneratedReport) {
        setIsDownloadEnabled(false);
      }
    }
  }, [fromDate, toDate, selectedUser]);

  const isFormValid = () => {
    return fromDate && toDate && selectedUser && !isReportJustGenerated;
  };

  const handleDownloadPDF = () => {
    if (!reportData) return;

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    
    // Title
    doc.setFontSize(14);
    doc.setTextColor(40, 40, 40);
    doc.text("Employee Tracking Report", pageWidth / 2, 20, { align: "center" });
    
    // Employee and Date Info
    doc.setFontSize(9);
    doc.setTextColor(80, 80, 80);
    doc.text(`Employee: ${selectedUser.name}`, 14, 35);
    doc.text(`Period: ${fromDate.toLocaleDateString("en-GB")} - ${toDate.toLocaleDateString("en-GB")}`, 14, 42);
    doc.text(`Generated: ${new Date().toLocaleDateString("en-GB")} ${new Date().toLocaleTimeString()}`, 14, 49);
    
    // Summary Section
    doc.setFontSize(11);
    doc.setTextColor(40, 40, 40);
    doc.text("Summary", 14, 62);
    
    const summaryData = [
      ["Base Times", ""],
      ["Total Work Schedule Time", reportData.summaryData.totalWorkScheduleTime || "0h 0m"],
      ["Total Break Time", reportData.summaryData.totalBreakTime || "0h 0m"],
      ["Total Allowed Break Time", reportData.summaryData.totalAllowedBreakTime || "0h 0m"],
      ["Total Over Time", reportData.summaryData.totalOverTime || "0h 0m"],
      ["", ""],
      ["Time With Actual Breaks", ""],
      ["Including Overtime", reportData.summaryData.totalWorkedTimeWithActualBreakIncludeOT || "0h 0m"],
      ["Excluding Overtime", reportData.summaryData.totalWorkedTimeWithActualBreakExcludeOT || "0h 0m"],
      ["", ""],
      ["Time With Allowed Breaks", ""],
      ["Including Overtime", reportData.summaryData.totalWorkedTimeWithAllowedBreakIncludeOT || "0h 0m"],
      ["Excluding Overtime", reportData.summaryData.totalWorkedTimeWithAllowedBreakExcludeOT || "0h 0m"],
      ["", ""],
      ["Application Usage", ""],
      ["Allowed Application Usage Time", reportData.summaryData.allowedAppUsageTime || "0h 0m"],
      ["Authorised Application Usage", reportData.summaryData.authorisedAppUsageTime || "0h 0m"],
      ["Unauthorised Application Usage", reportData.summaryData.unauthorisedAppUsageTime || "0h 0m"],
    ];

    autoTable(doc, {
      startY: 68,
      head: [["Metric", "Value"]],
      body: summaryData,
      theme: "grid",
      headStyles: {
        fillColor: [79, 129, 189],
        textColor: [255, 255, 255],
        fontSize: 9,
        fontStyle: "bold",
        halign: "center",
      },
      bodyStyles: {
        fontSize: 8,
        textColor: [60, 60, 60],
      },
      columnStyles: {
        0: { cellWidth: 100, fontStyle: "bold", fillColor: (rowIndex) => {
          if (summaryData[rowIndex][1] === "") return [233, 237, 241];
          return [255, 255, 255];
        }},
        1: { cellWidth: 80, halign: "center" },
      },
      alternateRowStyles: {
        fillColor: [249, 249, 249],
      },
      margin: { left: 14, right: 14 },
    });

    // Daily Activity Section
    doc.addPage();
    doc.setFontSize(11);
    doc.setTextColor(40, 40, 40);
    doc.text("Daily Activity Report", 14, 20);

    const dailyData = reportData.summaryData.daily.map((row) => [
      new Date(row.date).toLocaleDateString("en-GB"),
      row.workScheduleTime || "0h 0m",
      row.workedHours || "0h 0m",
      row.breakHours || "0h 0m",
      row.overtime || "0h 0m",
    ]);

    autoTable(doc, {
      startY: 28,
      head: [["Date", "Work Schedule", "Tracked Time", "Break Time", "Overtime"]],
      body: dailyData,
      theme: "grid",
      headStyles: {
        fillColor: [79, 129, 189],
        textColor: [255, 255, 255],
        fontSize: 8,
        fontStyle: "bold",
        halign: "center",
      },
      bodyStyles: {
        fontSize: 7,
        textColor: [60, 60, 60],
        halign: "center",
      },
      columnStyles: {
        0: { cellWidth: 30 },
        1: { cellWidth: 35 },
        2: { cellWidth: 35 },
        3: { cellWidth: 30 },
        4: { cellWidth: 30 },
      },
      alternateRowStyles: {
        fillColor: [249, 249, 249],
      },
      margin: { left: 14, right: 14 },
    });

    // Footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text(
        `Page ${i} of ${pageCount}`,
        pageWidth / 2,
        doc.internal.pageSize.getHeight() - 10,
        { align: "center" }
      );
    }

    const employeeName = selectedUser.name.replace(/\s+/g, "_");
    const fileName = `${employeeName}_Report_${fromDate.toISOString().split("T")[0]}_to_${toDate.toISOString().split("T")[0]}.pdf`;
    doc.save(fileName);
  };

  return (
    <div className="row bg-white rounded shadow pt-3 px-3">
      <div className="col-12 col-md-12 d-flex justify-content-between p-0">
        <h1 className="fw-bold">Employee Tracking Report</h1>
        <ArthaCancelButton
          className="px-2"
          onClick={isSuspended ? undefined : handleDownloadPDF}
          disabled={!isDownloadEnabled || isSuspended}
          title={isSuspended ? "Download disabled - Organization is suspended" : ""}
          style={{
            opacity: (isDownloadEnabled && !isSuspended) ? 1 : 0.5,
            cursor: (isDownloadEnabled && !isSuspended) ? "pointer" : "not-allowed",
          }}
        >
          <Download size={13} /> &nbsp; Download PDF
        </ArthaCancelButton>
      </div>

      <div className="row p-0 bg-white my-2 pb-1 border rounded d-flex m-0">
        {/* From Date Picker */}
        <div className="col-12 col-md-2 mb-2 pt-2">
          <label className="form-label">From Date</label>
          <DatePicker
            selected={fromDate}
            onChange={(date) => setFromDate(date)}
            onChangeRaw={handleDatePickerRawChange("dd/MM/yyyy")}
            className="form-control"
            dateFormat="dd/MM/yyyy"
            maxDate={toDate}
            maxLength={dateFormatMaxLength("dd/MM/yyyy")}
            withPortal
          />
        </div>

        {/* To Date Picker */}
        <div className="col-12 col-md-2 mb-2 pt-2">
          <label className="form-label">To Date</label>
          <DatePicker
            selected={toDate}
            onChange={(date) => setToDate(date)}
            onChangeRaw={handleDatePickerRawChange("dd/MM/yyyy")}
            className="form-control"
            dateFormat="dd/MM/yyyy"
            maxDate={new Date()}
            minDate={fromDate}
            maxLength={dateFormatMaxLength("dd/MM/yyyy")}
            withPortal
          />
        </div>

        {/* User Selection Dropdown */}
        <div className="col-12 col-md-5 mb-2 pt-2">
          <label className="form-label">Select User</label>
          <Select
            options={users}
            value={selectedUser}
            onChange={setSelectedUser}
            getOptionLabel={(option) => option.name}
            getOptionValue={(option) => option.code}
            placeholder="Select a user..."
            className="w-full border"
            styles={{
              control: (base) => ({
                ...base,
                height: "32px",
                minHeight: "32px",
                fontSize: "12px",
              }),
              dropdownIndicator: (base) => ({
                ...base,
                padding: "6px",
              }),
              clearIndicator: (base) => ({
                ...base,
                padding: "6px",
              }),
              input: (base) => ({
                ...base,
                margin: "0px",
                padding: "0px",
              }),
              valueContainer: (base) => ({
                ...base,
                padding: "0 6px",
              }),
            }}
          />
        </div>

        {/* Generate Report Button */}
        <div className="col-12 col-md-3 mb-2 d-flex align-items-end">
          <ArthaContinueButton
            onClick={isSuspended ? undefined : handleGenerateReport}
            className="shadow-sm w-100"
            disabled={!isFormValid() || isSuspended}
            title={isSuspended ? "Action disabled - Organization is suspended" : ""}
            style={{
              opacity: (isFormValid() && !isSuspended) ? 1 : 0.5,
              cursor: (isFormValid() && !isSuspended) ? "pointer" : "not-allowed",
            }}
          >
            Generate Report
          </ArthaContinueButton>
        </div>
      </div>

      {reportData ? (
        <div>
          {/* Second Accordion - Table */}
          <div className="row mt-2 bg-white rounded border ">
            <div
              className="px-3 py-2 bg-gray-100 border-b cursor-pointer flex justify-between items-center rounded"
              onClick={() => setIsFirstAccordionOpen(!isFirstAccordionOpen)}
            >
              <h5 className="m-0 font-medium">Activity Report</h5>
              {isFirstAccordionOpen ? (
                <ChevronUp size={20} />
              ) : (
                <ChevronDown size={20} />
              )}
            </div>

            {isFirstAccordionOpen && (
              <div className="px-3">
                <div className="row overflow-x-auto">
                  <table className="min-w-full table-auto border-collapse">
                    <thead className="bg-gray-100">
                      <tr className="border-b-2 border-gray-300">
                        <th className="col-2 px-4 py-2 font-semibold text-gray-700">
                          Date
                        </th>
                        <th className="col-3 px-4 py-2 font-semibold text-gray-700">
                          Work Schedule Time
                        </th>
                        <th className="col-3 px-4 py-2 font-semibold text-gray-700">
                          Total Tracked Time
                        </th>
                        <th className="col-2 px-4 py-2 font-semibold text-gray-700">
                          Break Time
                        </th>
                        <th className="col-2 px-4 py-2 font-semibold text-gray-700">
                          Overtime
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white">
                      {currentTableData &&
                        currentTableData.map((row, index) => (
                          <tr key={index} className="border-b hover:bg-gray-50">
                            <td className="px-4 py-2 text-gray-800">
                              {new Date(row.date).toLocaleDateString("en-GB")}
                            </td>
                            <td className="px-4 py-2 text-gray-800">
                              {row.workScheduleTime || "0h 0m"}
                            </td>
                            <td className="px-4 py-2 text-gray-800">
                              {row.workedHours || "0h 0m"}
                            </td>
                            <td className="px-4 py-2 text-gray-800">
                              {row.breakHours || "0h 0m"}
                            </td>
                            <td className="px-4 py-2 text-gray-800">
                              {row.overtime || "0h 0m"}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
                {/* {totalPages > 0 && (
                <div className="flex justify-center ">
                  {Array.from({ length: totalPages }, (_, i) => (
                    <button
                      key={i + 1}
                      onClick={() => setCurrentPage(i + 1)}
                      className={`px-3 py-1 rounded ${
                        currentPage === i + 1
                          ? "bg-orange-1000 text-white"
                          : "bg-gray-200"
                      }`}
                    >
                      {i + 1}
                    </button>
                  ))}
                </div>
              )} */}
              </div>
            )}
          </div>

          {/*  First Accordion - Sub Accordions */}
          <div className="row  bg-white my-3 rounded border ">
            <div
              className="px-3 py-2 bg-gray-100 border-b cursor-pointer flex justify-between items-center"
              onClick={() => setIsSecondAccordionOpen(!isSecondAccordionOpen)}
            >
              <h5 className="m-0 font-medium">Summary Totals</h5>
              {isSecondAccordionOpen ? (
                <ChevronUp size={20} />
              ) : (
                <ChevronDown size={20} />
              )}
            </div>

            {isSecondAccordionOpen && (
              <div className="">
                {/* Sub Accordions */}
                {[
                  {
                    key: "first",
                    title: "Base Times",
                    content: reportData && (
                      <div className="w-full rounded">
                        <div className="px-3 pb-2 border-b flex justify-between items-center">
                          <span>Total Work Schedule Time</span>
                          <span className="font-semibold">
                            {reportData.summaryData.totalWorkScheduleTime}
                          </span>
                        </div>
                        <div className="px-3 py-2 border-b flex justify-between items-center">
                          <span>Total Break Time</span>
                          <span className="font-semibold">
                            {reportData.summaryData.totalBreakTime}
                          </span>
                        </div>
                        <div className="px-3 py-2 border-b flex justify-between items-center">
                          <span>Total Allowed Break Time</span>
                          <span className="font-semibold">
                            {reportData.summaryData.totalAllowedBreakTime}
                          </span>
                        </div>
                        <div className="px-3 pt-2 flex justify-between items-center">
                          <span>Total Over Time</span>
                          <span className="font-semibold">
                            {reportData.summaryData.totalOverTime}
                          </span>
                        </div>
                      </div>
                    ),
                  },
                  {
                    key: "second",
                    title: "Time With Actual Breaks",
                    content: reportData && (
                      <div className="w-full rounded">
                        <div className="px-3 pb-2 border-b flex justify-between items-center">
                          <span>Including Overtime</span>
                          <span className="font-semibold">
                            {
                              reportData.summaryData
                                .totalWorkedTimeWithActualBreakIncludeOT
                            }
                          </span>
                        </div>
                        <div className="px-3 pt-2 flex justify-between items-center">
                          <span>Excluding Overtime</span>
                          <span className="font-semibold">
                            {
                              reportData.summaryData
                                .totalWorkedTimeWithActualBreakExcludeOT
                            }
                          </span>
                        </div>
                      </div>
                    ),
                  },
                  {
                    key: "third",
                    title: "Time With Allowed Breaks",
                    content: reportData && (
                      <div className="w-full rounded">
                        <div className="px-3 pb-2 border-b flex justify-between items-center">
                          <span>Including Overtime</span>
                          <span className="font-semibold">
                            {
                              reportData.summaryData
                                .totalWorkedTimeWithAllowedBreakIncludeOT
                            }
                          </span>
                        </div>
                        <div className="px-3 pt-2 flex justify-between items-center">
                          <span>Excluding Overtime</span>
                          <span className="font-semibold">
                            {
                              reportData.summaryData
                                .totalWorkedTimeWithAllowedBreakExcludeOT
                            }
                          </span>
                        </div>
                      </div>
                    ),
                  },
                  {
                    key: "fourth",
                    title: "Application Usage",
                    content: reportData && (
                      <div className="w-full rounded mb-2">
                        <div className="px-3 pb-2 border-b flex justify-between items-center">
                          <span>Allowed Application Usage Time</span>
                          <span className="font-semibold">
                            {reportData.summaryData.allowedAppUsageTime}
                          </span>
                        </div>
                        <div className="px-3 py-2 border-b flex justify-between items-center">
                          <span>Authorised Application Usage</span>
                          <span className="font-semibold">
                            {reportData.summaryData.authorisedAppUsageTime}
                          </span>
                        </div>
                        <div className="px-3 pt-2 flex justify-between items-center">
                          <span>Unauthorised Application Usage</span>
                          <span className="font-semibold">
                            {reportData.summaryData.unauthorisedAppUsageTime}
                          </span>
                        </div>
                      </div>
                    ),
                  },
                ].map((accordion) => (
                  <div
                    key={accordion.key}
                    className="border-b last:border-b-0  rounded"
                  >
                    <div
                      className="px-3 py-3 fw-semibold cursor-pointer flex justify-between items-center "
                      onClick={() =>
                        setSubAccordions({
                          ...subAccordions,
                          [accordion.key]: !subAccordions[accordion.key],
                        })
                      }
                    >
                      <h6 className="m-0">{accordion.title}</h6>
                      {subAccordions[accordion.key] ? (
                        <ChevronUp size={16} />
                      ) : (
                        <ChevronDown size={16} />
                      )}
                    </div>
                    {subAccordions[accordion.key] && (
                      <div className="px-3 py-2 bg-gray-50">
                        {accordion.content || (
                          <p>Content for {accordion.title}</p>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="col-12 col-md-12 mx-auto px-5 py-3 mt-4">
          <UserImage
            alt="Image Not Found"
            className="w-50 h-50 mx-auto px-5 py-2"
          />
          <p className="fw-bold text-center mt-3">
            Kindly generate the report to view it here.
          </p>
        </div>
      )}
    </div>
  );
}

export default EmployeeReportTracking;
